import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useDataApi } from "@core/apis";
import {
  getFlexStationParametersOptions,
  getSensorsPerSubstationOptions,
  getTemperatureAveragesOptions,
  getTimeSeriesTemperaturesOptions,
  normalizeSensorCounts,
  normalizeTemperatureSeries,
  type OverviewSensorCounts,
  type OverviewTemperatures,
} from "@core/apis/shared";

type OverviewOptions = {
  /**
   * FlexStation UID for the target temperature lookup.
   * - PropAI: resolve via `useGetFlexStations(substationId)` and pass `flexStations?.[0]?.uid`.
   * - Platform: pass `station?.subUid` from `FlexStationPageProps`.
   */
  flexStationUid?: string | null;
  /**
   * Network UID required by the Platform API for flex station parameter queries.
   * Omit on PropAI; PropAI does not need a network scope.
   */
  networkUid?: string;
};

/**
 * Sensor counts (total + active in last 1h) for the Overview header.
 *
 * Uses `getSensorsBySubstation` which is implemented identically on both
 * PropAI and Platform APIs. No app-specific options required.
 */
export const useOverviewSensors = (substationId: string) => {
  const dataApi = useDataApi();

  const { data: flexStationsData, isLoading } = useQuery({
    ...getSensorsPerSubstationOptions(dataApi, substationId),
    enabled: !!dataApi && !!substationId,
  });

  const data: OverviewSensorCounts = normalizeSensorCounts(flexStationsData);

  return { data, isLoading };
};

/**
 * Temperature KPIs (target / current / 7d / 24h + sparklines) for the Overview tab.
 *
 * @param substationId - Substation identifier (passed to all temperature queries).
 * @param options.flexStationUid - Required for the target-temperature lookup AND the
 *   flexstation-scoped `temperature-averages` endpoint. Caller resolves it.
 * @param options.networkUid - Required when running against the Platform API.
 *
 * @remarks The current (1h) / 24h / 7d averages come from `getTemperatureAverages`
 * (one BE call, implemented on both PropAI and Platform); sparklines come from
 * `getAverageSensorData`.
 */
export const useOverviewTemperatures = (substationId: string, options?: OverviewOptions) => {
  const dataApi = useDataApi();
  const { flexStationUid, networkUid } = options ?? {};

  // Target temperature from flex station parameters
  const { data: flexParameters, isLoading: isLoadingTarget } = useQuery(
    getFlexStationParametersOptions(dataApi, flexStationUid ?? null, substationId, networkUid)
  );

  // Backend-computed averages (current 1h, 24h, 7d) in a single call
  const { data: tempAverages, isLoading: isLoadingAverages } = useQuery(
    getTemperatureAveragesOptions(dataApi, substationId, flexStationUid, networkUid)
  );

  // Time series for sparklines
  const { data: tempTimeSeries, isLoading: isLoadingTimeSeries } = useQuery({
    ...getTimeSeriesTemperaturesOptions(dataApi, substationId),
    enabled: !!dataApi && !!substationId,
  });

  const isLoading = isLoadingTarget || isLoadingAverages || isLoadingTimeSeries;

  const data: OverviewTemperatures = useMemo(
    () => ({
      targetTemperature: flexParameters?.indoor_temp?.min ?? null,
      currentAvgTemp: tempAverages?.current ?? null,
      avgTemp7d: tempAverages?.avgTemp7d ?? null,
      avgTemp24h: tempAverages?.avgTemp24h ?? null,
      sparkline7d: normalizeTemperatureSeries(tempTimeSeries?.timeSeries7d),
      sparkline24h: normalizeTemperatureSeries(tempTimeSeries?.timeSeries24h),
    }),
    [flexParameters, tempAverages, tempTimeSeries]
  );

  return { data, isLoading };
};
